// TODO: make error states. we need a toast or validation zone
import { RoundedButton } from '@/components/rounded-button';

import { RoundedBadge } from '@/components/rounded-badge';
import { TooltipText } from '@/components/tooltip-text';
import { wrappedPlumeSymbol, wrappedPlumeTokenAddress } from '@/constants/shared';
import { nucleusClient } from '@/network/clients/nucleus';
import { getIconPathFromTokenSymbol, isTruthy } from '@/utils';
import { Card, FormatNumber, Heading, HStack, Icon, Image, Stack, Text } from '@chakra-ui/react';
import { Vault } from '@molecularlabs/nucleus-frontend';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { FiArrowRight, FiPlus } from 'react-icons/fi';
import { PiChartBarFill, PiHourglassSimpleHighFill } from 'react-icons/pi';
import { useSearchParams } from 'react-router';
import { TokenOption } from '../../types';
import { TransactionStep } from '../TransactionStep';
import { useCurrentVault } from '../hooks/useCurrentVault';
import { useDeposits } from '../hooks/useDeposits';

export const DepositSteps = ({
  amount,
  depositToken,
  vault,
  onCancel,
}: {
  amount: number;
  depositToken: TokenOption | undefined;
  vault: Vault;
  onCancel: () => void;
}) => {
  const {
    approveVaultSpending,
    approveRoycoSpending,
    roycoDepositTxHash,
    depositTxHash,
    isPendingApproval,
    isPendingDepositReceipt,
    isPendingRoycoApproval,
    isPendingRoycoDepositReceipt,
    isLoadingRoycoApprovalData,
    isLoadingVaultApprovalData,
    vaultSpendingApproved,
    roycoSpendingApproved,
    exchangeRate,
    depositIntoRoyco,
    depositIntoVault,
  } = useDeposits({ amount, depositToken });

  const { vaultAddress, name, vaultKey } = useCurrentVault();

  const { data: apyData } = useQuery({
    queryKey: ['vaultNetApy', vaultAddress],
    queryFn: async () => {
      if (!vaultAddress) return null;
      const apyResponse = await nucleusClient.get('plume/net-apy', {
        vaultAddress,
      });
      return apyResponse;
    },
  });

  const { underlyingRate = 0, boostIncentives } = apyData ?? {};
  const bonusIncentiveRate = boostIncentives?.[wrappedPlumeTokenAddress] ?? 0;

  const [searchParams] = useSearchParams();
  const roycoOnly = searchParams.get('royco_only') === 'true';

  const approveAndDepositVault = useCallback(async () => {
    if (!vaultSpendingApproved) await approveVaultSpending();
    await depositIntoVault();
  }, [approveVaultSpending, depositIntoVault, vaultSpendingApproved]);

  const approveAndDepositRoyco = useCallback(async () => {
    if (!roycoSpendingApproved) await approveRoycoSpending();
    await depositIntoRoyco();
  }, [approveRoycoSpending, depositIntoRoyco, roycoSpendingApproved]);

  const depositIntoOptimizerVaultComplete = Boolean(depositTxHash) && !isPendingDepositReceipt;
  const depositIntoRoycoComplete = Boolean(roycoDepositTxHash) && !isPendingRoycoDepositReceipt;

  // TODO: this is an imperfect abstraction. there's enough nuance between royco and vault steps
  // that they can be their own components, or at least be split into types
  // where certain properties can be imputed from types (such as icon which is only ever optimizer or royco)
  // eventually refactor this
  const transactionSteps = useMemo(() => {
    const steps = [];
    if (!roycoOnly)
      steps.push({
        icon: '/assets/vaults/yield-optimizer.svg',
        title: `Deposit into ${name}`,
        description: 'and earn base Vault yield',
        slotRight: (
          <HStack>
            <Icon boxSize={5} color="fg.muted">
              <PiChartBarFill />
            </Icon>
            <TooltipText
              fontSize="md"
              text={
                <Text color="fg" fontWeight="500">
                  <FormatNumber value={underlyingRate} style="percent" />{' '}
                  <Text color="fg.muted" as="span">
                    APY
                  </Text>
                </Text>
              }
              content="Base Vault Yield"
            />
          </HStack>
        ),
        complete: depositIntoOptimizerVaultComplete,
        loading: isPendingApproval || isLoadingVaultApprovalData || isPendingDepositReceipt,
        overview: (
          <Stack direction={['column', 'column', 'row']} fontSize={['sm', 'sm', 'md']}>
            <HStack>
              <Image src={getIconPathFromTokenSymbol(depositToken?.symbol ?? '')} boxSize={5} />
              <FormatNumber value={amount} />
              <Text color="fg.muted">{depositToken?.symbol}</Text> <FiArrowRight />{' '}
            </HStack>
            <HStack>
              <Image src={getIconPathFromTokenSymbol(vault.token.symbol)} boxSize={5} />
              <FormatNumber value={amount * exchangeRate} /> <Text color="fg.muted">{vaultKey}</Text>
            </HStack>
          </Stack>
        ),
        started: true,
        ctas: [{ action: approveAndDepositVault, name: 'Approve & Deposit into Optimizer Vault', variant: 'solid' }],
      });
    steps.push({
      icon: '/assets/vaults/royco-inverted.svg',
      title: 'Deposit into Royco Market',
      description: 'and earn PLUME incentives',
      slotRight: (
        <HStack>
          <Image src={getIconPathFromTokenSymbol(wrappedPlumeSymbol)} boxSize={5} />
          <TooltipText
            fontSize="md"
            text={
              <Text color="fg" fontWeight="500">
                <FormatNumber value={bonusIncentiveRate} style="percent" />{' '}
                <Text color="fg.muted" as="span">
                  APY
                </Text>
              </Text>
            }
            content="Royco Boosted Yield"
          />
        </HStack>
      ),
      complete: depositIntoRoycoComplete,
      loading: isPendingRoycoApproval || isLoadingRoycoApprovalData || isPendingRoycoDepositReceipt,
      overview: (
        <Stack direction={['column', 'column', 'row']} fontSize={['sm', 'sm', 'md']}>
          <HStack>
            <Image src={getIconPathFromTokenSymbol(vault.token.symbol)} boxSize={5} />
            <FormatNumber value={amount * exchangeRate} /> <Text color="fg.muted">{vaultKey}</Text>
            <FiPlus />
          </HStack>
          <HStack>
            <Image src={getIconPathFromTokenSymbol(wrappedPlumeSymbol)} boxSize={5} />
            <Text color="fg.muted">{wrappedPlumeSymbol}</Text>
            <RoundedBadge color="fg.muted">
              <PiHourglassSimpleHighFill /> 90 days
            </RoundedBadge>
          </HStack>
        </Stack>
      ),
      slotBottom: (
        <Text color="fg.muted" fontWeight="500">
          Withdraw anytime, wait{' '}
          <Text as="span" color="fg">
            90 days
          </Text>{' '}
          to earn PLUME incentives
        </Text>
      ),
      started: depositIntoOptimizerVaultComplete || roycoOnly,
      ctas: [
        !roycoOnly && { action: onCancel, name: 'Skip PLUME Incentives For Now', variant: 'outline' },
        { action: approveAndDepositRoyco, name: 'Approve & Deposit into Royco', variant: 'solid' },
      ].filter(isTruthy),
    });
    return steps;
  }, [
    amount,
    approveAndDepositRoyco,
    approveAndDepositVault,
    bonusIncentiveRate,
    depositIntoOptimizerVaultComplete,
    depositIntoRoycoComplete,
    depositToken?.symbol,
    exchangeRate,
    isLoadingRoycoApprovalData,
    isLoadingVaultApprovalData,
    isPendingApproval,
    isPendingDepositReceipt,
    isPendingRoycoApproval,
    isPendingRoycoDepositReceipt,
    name,
    onCancel,
    roycoOnly,
    underlyingRate,
    vault.token.symbol,
    vaultKey,
  ]);

  const currentStep = useMemo(() => transactionSteps.find(({ complete }) => !complete), [transactionSteps]);
  const Actions = () => {
    if (!currentStep) {
      return (
        <RoundedButton color="fg.inverted" variant="solid" w="full" onClick={onCancel} h={16} fontSize="md">
          Complete
        </RoundedButton>
      );
    }
    return currentStep.ctas.map(({ action, name, variant }) => (
      // TODO: get rid of typecasting by making proper types for transactionsteps
      <RoundedButton
        onClick={action}
        h={16}
        flex={1}
        variant={variant as 'solid' | 'outline'}
        loading={currentStep.loading}
      >
        {name}
      </RoundedButton>
    ));
  };

  return (
    <Card.Root w={['full', 'full', 620]} borderRadius="3xl">
      <Card.Header textAlign="center">
        <Heading size="2xl" fontWeight="700">
          Deposit Steps
        </Heading>
        <Heading color="fg.muted" fontSize="lg" fontWeight="500">
          Do not close this window when the transaction is in progress
        </Heading>
      </Card.Header>
      <Card.Body spaceY={4}>
        {transactionSteps.map((step, idx) => {
          return <TransactionStep key={step.title} stepNumber={idx + 1} {...step} />;
        })}
      </Card.Body>
      <Card.Footer>
        {/* if the user has clicked deposit into vault, the only way to revert the action would be to withdraw */}
        <Actions />
      </Card.Footer>
    </Card.Root>
  );
};
